// Pure aggregation helpers for the sidebar's home-scope view, extracted from
// Sidebar.tsx during the P5 split. Each helper takes plain data (channelMap,
// mutedSet, unread/mention/lastMessage maps, workspaceList) and returns a
// derived value the home content needs. Pure — no React, no hooks.

interface HomeChannelLike {
  id: string;
  workspace_id?: string | null;
  is_archived?: boolean | null;
}

export interface WorkspaceUnreadStats {
  unread: number;
  mentions: number;
}

interface WorkspaceLike {
  id: string;
  created_at?: number | null;
}

/**
 * Sum unread + mention counts across non-archived, non-muted channels,
 * grouped by workspace. Used for the home-scope workspace badges.
 */
export function aggregateWorkspaceUnreads(
  channels: HomeChannelLike[],
  mutedSet: Set<string>,
  unreadCountMap: Map<string, number>,
  mentionCountMap: Map<string, number>,
): Map<string, WorkspaceUnreadStats> {
  const map = new Map<string, WorkspaceUnreadStats>();
  for (const ch of channels) {
    if (!ch.workspace_id) continue;
    if (mutedSet.has(ch.id)) continue;
    const existing = map.get(ch.workspace_id) ?? { unread: 0, mentions: 0 };
    existing.unread += unreadCountMap.get(ch.id) ?? 0;
    existing.mentions += mentionCountMap.get(ch.id) ?? 0;
    map.set(ch.workspace_id, existing);
  }
  return map;
}

/**
 * Most-recent activity timestamp per workspace, for home-scope "recent"
 * sorting. Falls back to workspace.created_at when no channel activity is
 * known so freshly-created workspaces still appear in the recent list.
 */
export function computeWorkspaceLastActivity(
  channels: HomeChannelLike[],
  lastMessageAtMap: Map<string, number>,
  workspaceList: ReadonlyArray<WorkspaceLike>,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const ch of channels) {
    if (!ch.workspace_id) continue;
    const ts = lastMessageAtMap.get(ch.id) ?? 0;
    const existing = map.get(ch.workspace_id) ?? 0;
    if (ts > existing) map.set(ch.workspace_id, ts);
  }
  for (const ws of workspaceList) {
    if (!map.has(ws.id) && ws.created_at) {
      map.set(ws.id, ws.created_at);
    }
  }
  return map;
}

/**
 * Pick the best workspace to seed the NewDM dialog's email lookup with.
 * Default = first workspace; overridden by whichever workspace has the most
 * recent activity per `workspaceLastActivity`.
 */
export function pickBestLookupWorkspace(
  workspaceList: ReadonlyArray<{ id: string }>,
  workspaceLastActivity: Map<string, number>,
): string | undefined {
  let bestId = workspaceList[0]?.id;
  let bestTs = 0;
  for (const ws of workspaceList) {
    const ts = workspaceLastActivity.get(ws.id) ?? 0;
    if (ts > bestTs) {
      bestTs = ts;
      bestId = ws.id;
    }
  }
  return bestId;
}
